'use client';

import { useSyncExternalStore } from 'react';
import { useTranslations } from 'next-intl';
import { Link, usePathname } from '@/i18n/navigation';
import { useQuality } from '@/components/shared/QualityProvider';
import { getIsIntroActive, subscribeIsIntroActive } from '@/lib/journeyState';

export function ViewToggle() {
  const pathname = usePathname();
  const t = useTranslations('ViewToggle');
  const { tier } = useQuality();
  // SSR returns `false` so the toggle is present in the initial HTML for
  // routes that don't run an intro (eg /grid). Once the client mounts
  // and IntroLogoBreak (if present) flips the signal, the toggle hides
  // for the duration of the explosion and slides back on completion.
  const introActive = useSyncExternalStore(
    subscribeIsIntroActive,
    getIsIntroActive,
    () => false,
  );
  const onGrid = pathname.startsWith('/grid');
  const target = onGrid ? '/' : '/grid';
  const label = onGrid ? t('toJourney') : t('toGrid');

  // Low tier (mobile by default) has no journey to switch to. Render
  // nothing instead of a "Ver em 3D" link that would only redirect back
  // to /grid on click — same logic as the server-side redirect in
  // app/[locale]/page.tsx, kept in sync here.
  if (tier === 'low' || tier === 'pending') return null;

  // Fade out (rather than unmount) while the intro plays so the toggle
  // doesn't reflow the header layout when it reappears. pointer-events
  // also disable during the fade so the link isn't clickable through
  // a transparent box.
  const visible = !introActive;

  // No aria-label: the visible text is already a clear, full link label, and
  // adding a different aria-label triggers axe's label-content-name-mismatch
  // (voice-control users can't say a label they can't see).
  return (
    <Link
      href={target}
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      className={`text-xs uppercase tracking-widest text-(--color-muted-foreground) hover:text-(--color-accent) transition-opacity duration-[var(--duration-vt)] ease-[var(--ease-feedback)] ${
        visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      {label}
    </Link>
  );
}
